import i18next, { type i18n as Ii18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { KEY } from '../../constants/key';
import localizationData from './data.json';
import dayjs from './dayjs';

interface LocalizationEntry {
  projectId?: string;
  sortKey?: string;
  version?: string;
  tableId?: string;
  entryId?: number;
  smart?: boolean;
  key: string;
  value: { [x: string]: string };
}

type TransformedData = { [lang: string]: { [key: string]: string; }; };

export default class LocalizationManager {
  public static readonly i18n: Ii18n = i18next;
  private static _instance: LocalizationManager;
  private static _supportedLanguageCodes: string[] = [];

  private _initProcess?: Promise<void>;

  private constructor() { }

  public static get supportedLanguageCodes(): string[] {
    return LocalizationManager._supportedLanguageCodes;
  }

  public static getInstance = async (): Promise<LocalizationManager> => {
    if (!LocalizationManager._instance) {
      LocalizationManager._instance = new LocalizationManager();
      LocalizationManager._instance._initProcess ??= LocalizationManager._instance._init();

      await LocalizationManager._instance._initProcess;
    }

    return LocalizationManager._instance;
  };

  public static changeLanguage = async (lang?: string) => {
    await LocalizationManager.i18n.changeLanguage(lang);
    dayjs.locale(lang);

    if (lang) {
      localStorage.setItem(KEY.LOCALE_CODE, lang);
    } else {
      localStorage.removeItem(KEY.LOCALE_CODE);
    }
  };

  public static t = (
    key: string, values?: Array<string | number | boolean> | Record<string, string | number | boolean>
  ): string => LocalizationManager.i18n.t(key, this._normalizeValues(values));

  public static fetchCurrentLocalizationData = async () => {
    try {
      const baseUrl = 'https://api-localization.cdmvision.dev/api/v1';
      const projectId = 'e4bb4d5d-d2d9-4a86-9957-87ffc8e69082';
      const version = '1.0';
      const tableId = 'f3fadd4e-b00b-4dbc-9bb6-96ab6b0abdfb';
      const apiKey = 'D6E1C44BFECD4E62851F8F762D49709F';

      const url = `${baseUrl}/projects/${projectId}/versions/${version}/tables/${tableId}/entries`;
      const headers = { Authorization: `Basic ${apiKey}` };

      const response = await fetch(url, { headers });

      return (await response.json() ?? []) as LocalizationEntry[];

    } catch (error) {
      console.error(error);
      return [];
    }
  };

  private static _normalizeValues = (
    values?: Array<string | number | boolean> | Record<string, string | number | boolean>
  ): Record<string, string | number | boolean> | undefined => {
    if (!values) return undefined;

    if (Array.isArray(values)) {
      return values.reduce((acc, val, idx) => {
        acc[idx] = val;
        return acc;
      }, {} as Record<string, string | number | boolean>);
    }

    return values;
  };

  private _init = async () => {
    try {
      const localeCode = localStorage.getItem(KEY.LOCALE_CODE) ?? undefined;

      await LocalizationManager.i18n
        .use(initReactI18next)
        .init({
          lng: localeCode,
          fallbackLng: 'en',
          interpolation: { escapeValue: false },
          resources: {},
        });

      const data: LocalizationEntry[] = import.meta.env.DEV
        ? await LocalizationManager.fetchCurrentLocalizationData()
        : localizationData as LocalizationEntry[];

      const resources = this._addResources(data);

      dayjs.locale(LocalizationManager.i18n.language);

      LocalizationManager._supportedLanguageCodes = Object.keys(resources);

    } catch (error) {
      LocalizationManager._instance._initProcess = undefined;
      LocalizationManager._supportedLanguageCodes = [];
      throw error;
    }
  };

  private _addResources = (data: LocalizationEntry[]): TransformedData => {
    const resources: TransformedData = {};

    data.forEach((entry) => {
      const { key, value, smart } = entry;

      Object.entries(value).forEach(([lang, val]) => {
        resources[lang] ??= {};

        if (smart) {
          const forms = this._getPluralForms(lang);
          const parsed = this._parseValue(val, forms);

          if (typeof parsed === 'string') {
            resources[lang][key] = parsed;
          } else {
            for (const [subKey, subVal] of Object.entries(parsed)) resources[lang][`${key}_${subKey}`] = subVal;
          }
        } else {
          resources[lang][key] = val;
        }
      });
    });

    Object.entries(resources).forEach(([lang, entries]) => {
      Object.entries(entries).forEach(([key, value]) => {
        LocalizationManager.i18n.addResource(lang, 'translation', key, value);
      });
    });

    return resources;
  };

  private _getPluralForms = (lang: string): string[] => {
    const rule = LocalizationManager.i18n.services.pluralResolver.getRule(lang);
    if (!rule) return ['other'];

    if (typeof rule.resolvedOptions === 'function') {
      return (rule.resolvedOptions().pluralCategories as string[]) ?? ['other'];
    }

    return rule.forms ?? ['other'];
  };

  private _parseValue = (value: string, forms: string[]): Record<string, string> | string => {
    const match = value.match(/\{0:plural:([\s\S]+)\}/);
    if (!match) return this._normalizePlaceholders(value);
    if (!forms) return this._normalizePlaceholders(value);
    if (match.length != 2) console.error('Invalid localization plural format!');

    const variants = match[1].split('|');
    const output: Record<string, string> = {};

    const beforePattern = value.substring(0, match.index!);
    const afterPattern = value.substring(match.index! + match[0].length);
    const normalizedBefore = this._normalizePlaceholders(beforePattern);
    const normalizedAfter = this._normalizePlaceholders(afterPattern);

    forms.forEach((form, idx) => {
      const raw = variants[idx] ?? variants[variants.length - 1];
      const normalizedPlural = this._normalizePlaceholders(raw, true);
      output[form] = normalizedBefore + normalizedPlural + normalizedAfter;
    });

    return output;
  };

  private _normalizePlaceholders = (value: string, isPlural = false): string => {
    let normalizedValue = value;

    // for plurals {0} -> {{count}}
    if (isPlural) normalizedValue = normalizedValue.replace(/\{0\}/g, '{{count}}');

    // normal type inputs {{0}}, {{1}}
    return normalizedValue.replace(/\{(\d+)\}/g, '{{$1}}');
  };
}
